import { type UIEvent, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Box,
  ContainerActions,
  Icon,
  layoutStore,
  Scrollbar,
  SpinnerLoader,
  Typography,
} from 'shared/ui'
import { actionWakeMode } from 'shared/lib'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useEventEmitter } from 'entities/EventEmitter'
import { DropdownFilter } from 'features/DropdownFilter'
import { DropdownOrder } from 'features/DropdownOrder'
import { ConversationCard, ConversationNewCard } from 'pages/conversations/ui/ConversationCard'
import { SelectAction } from 'pages/conversations/ui/ConversationList/ui/SelectAction/SelectAction'
import { useConversationListContext } from 'pages/conversations/ui/ConversationList/context'
import { BulkActions } from 'pages/conversations/ui/ConversationList/ui/BulkActions'
import { useEventConversationReassigned } from 'pages/conversations/ui/ConversationList/events/EventConversationReassigned'
import { useEventMessageUpdatedNew } from '../events/EventMessageUpdatedNew'
import { useEventMessageReceivedNew } from '../events/EventMessageReceivedNew'
import { useEventVoicemailReceived } from '../events/EventVoicemailReceived'
import { useEventConversationUnread } from '../events/EventConversationUnread'
import { useEventConversationClosed } from '../events/EventConversationClosed'
import { useEventConversationOpened } from '../events/EventConversationOpened'
import { useEventConversationBulkFavorite } from '../events/EventConversationBulkFavorite'
import { useEventConversationMentionUnread } from '../events/EventConversationMentionUnread'
import styles from './styles.module.scss'

export type IConversationListVirtualizationOptions = {
  estimateSize?: number
  overscan?: number
  endReachedThreshold?: number
  measureItems?: boolean
  flushSyncOnScroll?: boolean
  skeletonWhileScrolling?: boolean
}

type IConversationListContentProps = {
  virtualized?: boolean | IConversationListVirtualizationOptions
}

export const ConversationListContent = observer(
  ({ virtualized }: IConversationListContentProps) => {
    const isVirtualized = Boolean(virtualized)
    const {
      estimateSize = 90,
      overscan = 12,
      endReachedThreshold = 8,
      measureItems = true,
      flushSyncOnScroll = false,
      skeletonWhileScrolling = false,
    } = virtualized && typeof virtualized === 'object' ? virtualized : {}

    const { conversationStore, usersStore, inboxesStore } = useEntitiesContext()
    const plainScrollbarRef = useRef<HTMLDivElement | null>(null)
    const { currentItemId } = conversationStore
    const store = useConversationListContext()
    const {
      updateConversations,
      searchConversations,
      handleScrollPosition,
      listOrders,
      loadingMore,
      loading,
      isEmpty,
      hasMore,
      hasSelected,
      scrollTrigger,
      onOpenConversation,
      showConversationsCount,
    } = store
    const { searchDropdownStore } = store.conversationSearchStore
    const typeInbox = inboxesStore.localInboxes.typeInbox

    const loadMore = () => {
      if (loading || loadingMore || !hasMore) return
      if (store.conversationSearchStore.hasSearchParams && !searchDropdownStore.isOpen) {
        if (store.conversationSearchStore.hasMore) searchConversations()
      } else {
        updateConversations()
      }
    }

    const handleScroll = (e: UIEvent) => {
      const target = e.target as HTMLDivElement
      handleScrollPosition(target.scrollTop)
      if (!isVirtualized && target.scrollHeight <= target.scrollTop + target.offsetHeight + 100) {
        loadMore()
      }
    }

    useEventConversationUnread(store, typeInbox)
    useEventMessageReceivedNew(store, typeInbox)
    useEventMessageUpdatedNew(store, typeInbox)
    useEventVoicemailReceived(store, typeInbox)
    useEventConversationMentionUnread(store, typeInbox)
    useEventConversationClosed(store, typeInbox)
    useEventConversationOpened(store, typeInbox)
    useEventConversationBulkFavorite(typeInbox)
    useEventConversationReassigned(store, typeInbox)

    useEventEmitter('updateConversationsFilter', (payload) => {
      store.filtersStore.setFilter(payload.id, true)
      store.changeQuickFilter(payload.id)
    })
    useEventEmitter('initConversationsList', () => {
      store.initReactions()
    })
    useEventEmitter('load_conversation_list', () => {
      store.load()
    })
    useEventEmitter('reset_conversation_list', () => {
      store.reset()
    })

    useEffect(() => {
      store.handleChangeFirstLoading(true)
      store.handleChangeVisibleUI(true)
      store.initReactions()

      return () => {
        store.handleChangeFirstLoading(false)
        store.handleChangeVisibleUI(false)
        store.reset()
        store.resetReactions()
      }
    }, [])

    useEffect(
      () =>
        actionWakeMode(() => {
          store.reset()
          store.load()
        }),
      []
    )

    useEffect(() => {
      if (isVirtualized) return
      if (plainScrollbarRef.current) plainScrollbarRef.current.scrollTop = store.scrollPosition
    }, [scrollTrigger, store, isVirtualized])

    const isShouldRequestAccess = !!usersStore.user?.isShouldRequestAccess
    const showActions =
      !store.conversationSearchStore.hasSearchParams && !hasSelected && !isShouldRequestAccess

    const renderCards = () =>
      listOrders.map((conversation) => {
        if (conversation.isNew) {
          return (
            <ConversationNewCard
              key={conversation.id}
              isActive={Number(currentItemId) === conversation.id}
            />
          )
        }

        return (
          <ConversationCard
            key={conversation.id}
            conversation={conversation}
            isActive={!layoutStore.isMobileView && Number(currentItemId) === conversation.id}
            onOpenAction={onOpenConversation}
          />
        )
      })

    const loadingMoreSpinner = loadingMore ? (
      <div className={styles.loadingMore}>
        <SpinnerLoader ariaLabel={'ConversationListContent'} size={16} />
      </div>
    ) : null

    return (
      <>
        {hasSelected && (
          <ContainerActions
            leftActions={[{ iconButtonComponent: <SelectAction /> }]}
            rightActions={[{ iconButtonComponent: <BulkActions /> }]}
            disabled={loading}
          />
        )}
        {store.conversationSearchStore.hasSearchParams && (
          <div className={styles.total}>
            <Typography variant={'body-md-medium'} ariaLabel={'results'}>
              {store.conversationSearchStore.total} results
            </Typography>
          </div>
        )}
        {showActions && (
          <ContainerActions
            disabled={loading}
            padding='8px 12px 0 8px'
            leftActions={[
              {
                iconButtonComponent: showConversationsCount ? (
                  <Box ml={'12px'}>
                    <Typography variant={'body-md-medium'} ariaLabel={'results'}>
                      {`${conversationStore.itemsTotalCount} Conversations`}
                    </Typography>
                  </Box>
                ) : (
                  <DropdownFilter store={store.filtersStore} />
                ),
              },
            ]}
            rightActions={[{ iconButtonComponent: <DropdownOrder store={store.ordersStore} /> }]}
          />
        )}
        {loading && !loadingMore && (
          <div className={styles.listStatus}>
            <SpinnerLoader ariaLabel={'ConversationListContent'} />
          </div>
        )}
        {!loading && isEmpty && !store.conversationSearchStore.hasSearchParams && (
          <div className={styles.listStatus}>
            <Icon icon={'chatEmpty'} fontSize={24} />
            {inboxesStore.localInboxes.hasQuickFilter ? (
              <p>No open conversations</p>
            ) : (
              <p>No conversations</p>
            )}
          </div>
        )}
        {!loading && isEmpty && store.conversationSearchStore.hasSearchParams && (
          <div className={styles.listStatus}>
            <Icon icon={'filter'} fontSize={24} />
            <p>No matching conversations</p>
          </div>
        )}

        {isVirtualized ? (
          <div className={styles.virtualizedWrap}>
            <Scrollbar
              className={styles.inboxesVirtualized}
              viewClassName={styles.inboxesVirtualizedView}
              trackVerticalClassName={styles.inboxesVirtualizedTrack}
              virtualized
              estimateSize={estimateSize}
              gap={2}
              overscan={overscan}
              paddingStart={7}
              paddingEnd={7}
              endReachedThreshold={endReachedThreshold}
              measureItems={measureItems}
              flushSyncOnScroll={flushSyncOnScroll}
              skeletonWhileScrolling={skeletonWhileScrolling}
              onScroll={handleScroll}
              onEndReached={loadMore}
            >
              {(!loading || loadingMore) && renderCards()}
            </Scrollbar>
            {loadingMoreSpinner}
          </div>
        ) : (
          <div
            className={`${styles.inboxes} ${loadingMore ? styles.loadingMoreList : ''}`}
            onScroll={handleScroll}
            ref={plainScrollbarRef}
          >
            {(!loading || loadingMore) && renderCards()}
            {loadingMoreSpinner}
          </div>
        )}
      </>
    )
  }
)
